import BaseProtable from "@/components/BaseProTable";
import { PrimaryButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import { notifyWarning } from "@/utils/notification";
import { type TableProps } from "antd";
import { useState } from "react";
import { usePageTable } from "@/hooks/baseProTable/usePageTable";
import { SupplierPageDto, SupplierPageVo } from "@/types/apis";
import { getSupplierPage } from "@/apis/supplier";
import { useCountryOptions } from "@/hooks/services/useSelectOptionsQuery";

type SelectItem = SupplierPageVo;
type SearchParams = SupplierPageDto;

type Props = {
  open: boolean;
  onOk: (selectedRows: SelectItem[]) => void;
  onClose: () => void;
  /** 选择模式，默认多选 */
  selectionMode?: "multiple" | "single";
};

const SupplierSelector: React.FC<Props> = ({
  open,
  onOk,
  onClose,
  selectionMode = "multiple",
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<SelectItem[]>([]);
  const [params, setParams] = useState<SearchParams>({
    // currentStageList: ["4"],
    registrationStatusList: ["1", "2", "3", "4"],
  });
  const countryOptions = useCountryOptions();

  const { actionRef, fetchDatasource, columns, formRef } = usePageTable<
    SelectItem,
    SearchParams
  >({
    fetchFn: getSupplierPage,
    columns: [
      {
        title: "序号",
        valueType: "index",
        width: 60,
        align: "center",
        fixed: "left",
      },
      {
        title: "搜索",
        dataIndex: "keyword",
        hideInTable: true,
        fieldProps: {
          placeholder: "请输入供应商编号或名称",
        },
        order: 100,
      },
      {
        title: "供应商编号",
        dataIndex: "supplierCode",
        width: 150,
        align: "center",
        search: false,
      },
      {
        title: "供应商名称",
        dataIndex: "supplierName",
        align: "center",
        search: false,
        render: (_, record) => {
          return record.supplierName || record.supplierUsName;
        },
      },
      {
        title: "所属国家",
        dataIndex: "country",
        order: 98,
        valueType: "select",
        fieldProps: {
          options: countryOptions,
          showSearch: true,
          optionFilterProp: "label",
        },
        hideInTable: true,
      },
      {
        title: "所属国家",
        dataIndex: "supplierCountry",
        search: false,
      },
      {
        title: "物料类型",
        dataIndex: "materialType",
        align: "center",
        hideInTable: true,
        valueType: "select",
        fieldProps: {
          options: [
            { label: "生产性物料", value: "PO" },
            { label: "非生产性物料", value: "nonPO" },
          ],
        },
        order: 99,
      },
      {
        title: "物料类型",
        dataIndex: "materialCategoryValue",
        key: "materialCategoryValue",
        align: "center",
        search: false,
        valueType: "select",
        fieldProps: {
          options: [
            { label: "生产性物料", value: "PO" },
            { label: "非生产性物料", value: "nonPO" },
          ],
        },
      },
    ],
  });

  const rowSelection: TableProps<SelectItem>["rowSelection"] = {
    type: selectionMode === "single" ? "radio" : "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectionMode === "single") {
        setSelectedRowKeys(selectedRowKeys.slice(0, 1));
        setSelectedRows(selectedRows.slice(0, 1));
        return;
      }
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };

  const handleOk = () => {
    if (selectedRows.length === 0) {
      notifyWarning("请选择选择需求单");
      return;
    }
    onOk?.(selectedRows);
    handleClose();
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setParams({});
    onClose();
  };

  const footer = () => (
    <div style={{ textAlign: "center" }}>
      <PrimaryButton onClick={handleOk}>确定</PrimaryButton>
    </div>
  );

  return (
    <CommonModal
      title="选择供应商"
      open={open}
      onCancel={handleClose}
      width={1200}
      style={{ top: 20 }}
      footer={footer}
    >
      <BaseProtable<SelectItem>
        headerTitle={null}
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        rowSelection={rowSelection}
        rowKey="id"
        bordered
        request={fetchDatasource}
        params={params}
        search={{
          labelWidth: "auto",
          span: 6,
        }}
        onSubmit={(values) =>
          setParams({
            // currentStageList: ["4"],
            registrationStatusList: ["1", "2", "3", "4"],
            ...values,
          })
        }
      />
    </CommonModal>
  );
};

export default SupplierSelector;
